import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminShell, RequireAdmin } from './AdminShell';
import { apiAuthed } from '../../lib/adminApi';
import { Loader2, PlayCircle, Sparkles, CheckCircle2, AlertTriangle, RefreshCcw } from 'lucide-react';

const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [loadError, setLoadError] = useState('');
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);
  const [healthError, setHealthError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiAuthed.get('/settings/publisher')
      .then((r) => { if (!cancelled) setS(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
        setLoadError(`Failed to load settings: ${detail}`);
        // Prevent forever-loading — render the page with an empty settings shell.
        setS({ auto_publish_enabled: false, frequency_days: 7, audience: '', topics_queue: [] });
      });
    return () => { cancelled = true; };
  }, []);

  const loadHealth = () => {
    setHealthBusy(true); setHealthError('');
    apiAuthed.get('/admin/health')
      .then((r) => setHealth(r.data))
      .catch((e) => setHealthError(e?.response?.data?.detail || e?.message || 'Health check failed'))
      .finally(() => setHealthBusy(false));
  };
  useEffect(() => { loadHealth(); }, []);

  const set = (patch) => setS((x) => ({ ...x, ...patch }));

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const topics = String(s.topics_queue_text ?? (s.topics_queue || []).join('\n'))
        .split(/\r?\n/).map(t => t.trim()).filter(Boolean);
      const { data } = await apiAuthed.put('/settings/publisher', {
        auto_publish_enabled: !!s.auto_publish_enabled,
        frequency_days: Math.max(1, Math.min(90, parseInt(s.frequency_days || 7, 10))),
        audience: s.audience || '',
        topics_queue: topics,
      });
      setS({ ...data, topics_queue_text: (data.topics_queue || []).join('\n') });
      setMsg('Saved.');
    } catch (e) {
      setMsg('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true); setMsg('');
    try {
      await apiAuthed.post('/settings/publisher/run-now');
      setMsg('Queued. The next hourly scheduler tick will generate and publish the next topic (or click the AI button in a new post to try instantly).');
    } catch (e) {
      setMsg('Run-now failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (!s) return (
    <RequireAdmin><AdminShell title="Publisher settings"><div className="flex items-center gap-2 text-body-muted"><Loader2 className="h-4 w-4 animate-spin"/> Loading…</div></AdminShell></RequireAdmin>
  );

  const topicsText = s.topics_queue_text ?? (s.topics_queue || []).join('\n');

  return (
    <RequireAdmin>
      <AdminShell title="Publisher settings" actions={
        <>
          <Link to="/admin/blog" className="text-body-muted hover:text-navy text-[12px] font-bold uppercase tracking-[0.08em]">← Posts</Link>
          <button type="button" onClick={runNow} disabled={busy} data-testid="settings-run-now"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-slate-300 text-navy text-[12px] font-bold uppercase tracking-[0.08em] hover:border-navy transition-colors disabled:opacity-60">
            <PlayCircle className="h-4 w-4"/> Reset cooldown
          </button>
          <button type="button" onClick={save} disabled={busy || !!loadError} data-testid="settings-save"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[12px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg transition-all duration-300 disabled:opacity-60">
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </>
      }>
        {loadError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-[13px]" data-testid="settings-load-error">
            {loadError}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-mk p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-brand-pink" />
              <h2 className="text-navy font-black text-[18px]">Auto-publish</h2>
            </div>
            <p className="text-body-muted text-[14px] leading-[1.6]">
              When on, an AI-written article is generated from the next topic in your queue and published automatically at the frequency you choose. The scheduler checks hourly.
            </p>
            <label className="mt-5 flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="settings-enabled" checked={!!s.auto_publish_enabled}
                onChange={(e) => set({ auto_publish_enabled: e.target.checked })}
                className="h-4 w-4 text-brand-pink focus:ring-brand-pink" />
              <span className="text-navy text-[14px]">Enable auto-publish</span>
            </label>
            <div className="mt-5">
              <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Frequency (days)</label>
              <input type="number" min={1} max={90} data-testid="settings-frequency" value={s.frequency_days}
                onChange={(e) => set({ frequency_days: e.target.value })}
                className="mt-1 w-32 h-10 bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[15px] outline-none"/>
              <p className="text-body-muted text-[12.5px] mt-1">A new post publishes at most once every N days.</p>
            </div>
            <div className="mt-5">
              <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-body-muted">Audience prompt</label>
              <input value={s.audience || ''} onChange={(e) => set({ audience: e.target.value })}
                data-testid="settings-audience"
                className="mt-1 w-full h-10 bg-transparent border-b-2 border-slate-200 focus:border-brand-blue text-navy text-[15px] outline-none"/>
            </div>
            {s.last_published_at && (
              <p className="mt-5 text-body-muted text-[12.5px]">Last auto-published: {new Date(s.last_published_at).toLocaleString('en-GB')}</p>
            )}
          </div>

          <div className="rounded-2xl bg-white border border-slate-100 shadow-mk p-6">
            <h2 className="text-navy font-black text-[18px] mb-2">Topics queue</h2>
            <p className="text-body-muted text-[14px] leading-[1.6]">One topic per line. The oldest is used first and removed after publishing. Add more any time.</p>
            <textarea rows={12} data-testid="settings-topics" value={topicsText}
              onChange={(e) => set({ topics_queue_text: e.target.value })}
              placeholder={"How B2B publishers can price lead-gen programmes\nBuilding a 90-day audience-extension pilot\nWhat SDR teams actually want from publisher leads"}
              className="mt-3 w-full rounded-lg border border-slate-200 focus:border-brand-blue outline-none p-3 text-navy text-[14.5px] leading-[1.65] font-mono"
            />
          </div>
        </div>

        {/* ---- System health (env / files / DB / scheduler) ---- */}
        <div className="mt-8 rounded-2xl bg-white border border-slate-100 shadow-mk p-6" data-testid="system-health">
          <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
            <div>
              <h2 className="text-navy font-black text-[18px]">System health</h2>
              <p className="text-body-muted text-[13.5px] leading-[1.6]">Which env vars this deploy has picked up. Values are masked — only the last 4 characters of secrets are shown.</p>
            </div>
            <button type="button" onClick={loadHealth} disabled={healthBusy} data-testid="health-refresh"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-slate-300 text-navy text-[12px] font-bold uppercase tracking-[0.08em] hover:border-navy transition-colors disabled:opacity-60">
              <RefreshCcw className={`h-3.5 w-3.5 ${healthBusy ? 'animate-spin' : ''}`}/> Refresh
            </button>
          </div>
          {healthError && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 text-red-700 px-4 py-2 text-[13px]" data-testid="health-error">{healthError}</div>
          )}
          {health && (
            <>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {health.env.map((row) => {
                  const ok = row.present;
                  const optional = !row.required;
                  const cls = ok
                    ? 'border-emerald-200 bg-emerald-50/60'
                    : optional
                      ? 'border-amber-200 bg-amber-50/60'
                      : 'border-red-200 bg-red-50/60';
                  const icon = ok
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0"/>
                    : <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${optional ? 'text-amber-600' : 'text-red-600'}`}/>;
                  const label = ok ? row.masked : (optional ? 'not set (optional)' : 'missing (required)');
                  return (
                    <div key={row.key} data-testid={`env-${row.key}`}
                      title={row.hint || ''}
                      className={`rounded-lg border px-3 py-2.5 flex items-start gap-2 ${cls}`}>
                      {icon}
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-navy">
                          {row.key}{optional && !ok ? ' · optional' : ''}
                        </p>
                        <p className="text-[12.5px] text-body-muted truncate font-mono">{label}</p>
                        {row.hint && <p className="text-[11.5px] text-body-muted/80 mt-0.5 leading-snug">{row.hint}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className={`rounded-lg border px-3 py-2.5 ${health.database?.ok ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/60'}`}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-navy">MySQL</p>
                  <p className="text-[12.5px] text-body-muted">{health.database?.ok ? 'reachable' : 'unreachable (fallback JSONL in use)'}</p>
                </div>
                <div className={`rounded-lg border px-3 py-2.5 ${health.files_writable ? 'border-emerald-200 bg-emerald-50/60' : 'border-red-200 bg-red-50/60'}`}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-navy">Files dir</p>
                  <p className="text-[12.5px] text-body-muted">{health.files_writable ? 'writable' : 'not writable'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-navy">Scheduler</p>
                  <p className="text-[12.5px] text-body-muted">
                    {health.scheduler?.auto_publish_enabled ? 'on' : 'off'} · every {health.scheduler?.frequency_days || '—'}d · {health.scheduler?.topics_in_queue ?? 0} queued
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {msg && <p className="mt-4 text-navy text-[13.5px]" data-testid="settings-message">{msg}</p>}
      </AdminShell>
    </RequireAdmin>
  );
};

export default AdminSettings;
